import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Button,
  Form,
  Input,
  Label,
  SearchField,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  type Key,
} from 'react-aria-components';
import { Button as UiButton, ButtonLink } from '../ui/Button';
import { cx } from '../ui/cx';
import { EmptyState } from '../ui/EmptyState';
import { Icon } from '../ui/Icon';
import { TextField } from '../ui/TextField';
import { HelpArticle } from '../help/HelpArticle';
import type { HelpAnchorRef, HelpSource, HelpTopic, PageHelp } from './types';
import s from './HelpPanel.module.css';

/** Who answers questions when Help doesn't: the Stock Admin team. */
export interface HelpContact {
  title: string;
  body: string;
  /** The people on the team, by name. */
  team: readonly string[];
  /** The team's shared mailbox, when a System Admin set one. */
  email: string | null;
  /**
   * Sends a message to the team from Help and returns who it went to. Left out when it can't be
   * sent (Preview, or nobody else on the team).
   */
  onSend?: (message: { subject: string; message: string }) => Promise<readonly string[]>;
}

/**
 * What the chat can ask of the panel: open a topic under Browse help, and point at something on
 * the page ("Show me", APP-090; false when it isn't there to show).
 */
export interface HelpChatHelpers {
  openTopic: (id: string) => void;
  showMe?: (anchor: HelpAnchorRef) => boolean;
}

export interface HelpPanelProps {
  /** Help for the page the person is on. */
  page: PageHelp;
  /** Every topic the person may see; Browse help lists them. */
  topics: readonly HelpTopic[];
  /** Loading state, popular topics and server search (APP-048). */
  source?: HelpSource;
  /** The Admin › AI Help chat switch. Off: no chat, and nothing is sent to a model. */
  aiHelpEnabled: boolean;
  /** The chat assistant. Rendered only when `aiHelpEnabled` is on. */
  chat?: ReactNode | ((helpers: HelpChatHelpers) => ReactNode);
  contact?: HelpContact;
  /** Opens Send feedback (APP-104 §3.1); the panel closes first, so the picture is of the page. */
  onFeedback?: () => void;
  /**
   * "Show me" (APP-090): scroll to the thing, highlight it and move focus there. The shell gives
   * it; on a narrow window it closes the panel first, as the panel covers the page there.
   */
  onShowMe?: (anchor: HelpAnchorRef) => boolean;
  onClose: () => void;
}

export const HELP_PANEL_ID = 'help-panel';

function matches(topic: HelpTopic, query: string): boolean {
  const haystack =
    `${topic.title} ${topic.summary} ${topic.category} ${topic.keywords ?? ''}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

/** "Ana", "Ana and Ben", "Ana, Ben and Cy", "Ana, Ben and 3 others". */
export function namesInWords(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length <= 3)
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
  return `${names.slice(0, 2).join(', ')} and ${String(names.length - 2)} others`;
}

/**
 * The right-side Help panel: Ask (the chat, when the Admin turned it on) and Browse help (this
 * page's topics, popular ones, a search over every topic, all topics, and the Stock Admin team).
 * Escape closes it.
 */
export function HelpPanel({
  page,
  topics,
  source,
  aiHelpEnabled,
  chat,
  contact,
  onFeedback,
  onShowMe,
  onClose,
}: HelpPanelProps): ReactElement {
  const panelRef = useRef<HTMLElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();
  const [tab, setTab] = useState<Key>('ask');
  const [wide, setWide] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const showChat = aiHelpEnabled && chat !== undefined;

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // The column fits the part of the window below whatever is still above the panel: the header
  // while it's in view, nothing once it has scrolled away (QA-026). The Ask box stays in view.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const inner = innerRef.current;
    if (panel === null || inner === null) return;
    let frame = 0;
    const measure = (): void => {
      frame = 0;
      const top = Math.max(0, panel.getBoundingClientRect().top);
      inner.style.setProperty('--help-top', `${String(Math.round(top))}px`);
    };
    const schedule = (): void => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target;
      if (target instanceof Node && panelRef.current?.contains(target)) {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const browse = (
    <BrowseHelp
      page={page}
      topics={topics}
      openId={openId}
      onOpen={setOpenId}
      {...(source && { source })}
      {...(contact && { contact })}
      {...(onFeedback && { onFeedback })}
      {...(onShowMe && { onShowMe })}
      {...(showChat && {
        onAskChat: () => {
          setTab('ask');
        },
      })}
    />
  );

  return (
    <aside
      id={HELP_PANEL_ID}
      ref={panelRef}
      className={cx(s.panel, wide && s.wide)}
      aria-labelledby={headingId}
    >
      <div ref={innerRef} className={s.inner}>
        <div className={s.head}>
          <div className={s.headText}>
            <p className={cx('eyebrow', s.pageLabel)}>{page.pageLabel}</p>
            <h2 id={headingId} ref={headingRef} tabIndex={-1} className={s.title}>
              Help
            </h2>
            <p className={s.headSub}>
              {showChat ? 'Ask a question or browse topics' : 'Topics for this page'}
            </p>
          </div>
          <div className={s.headActions}>
            <Button
              className={cx(s.close, s.widen)}
              aria-label={wide ? 'Narrow help' : 'Widen help'}
              aria-pressed={wide}
              onPress={() => {
                setWide((value) => !value);
              }}
            >
              <Icon name={wide ? 'minimize' : 'maximize'} size={18} />
            </Button>
            <Button className={s.close} onPress={onClose} aria-label="Close help">
              <Icon name="close" size={20} />
            </Button>
          </div>
        </div>
        {showChat ? (
          <Tabs selectedKey={tab} onSelectionChange={setTab}>
            <TabList aria-label="Help" className={s.tabList}>
              <Tab id="ask" className={s.tab}>
                Ask
              </Tab>
              <Tab id="browse" className={s.tab}>
                Browse help
              </Tab>
            </TabList>
            <TabPanel id="ask" className={s.body}>
              {typeof chat === 'function'
                ? chat({
                    openTopic: (id) => {
                      setOpenId(id);
                      setTab('browse');
                    },
                    ...(onShowMe && { showMe: onShowMe }),
                  })
                : chat}
            </TabPanel>
            <TabPanel id="browse" className={s.body}>
              {browse}
            </TabPanel>
          </Tabs>
        ) : (
          <div className={s.body}>{browse}</div>
        )}
      </div>
    </aside>
  );
}

function TopicButton({
  topic,
  showCategory,
  onOpen,
}: {
  topic: HelpTopic;
  showCategory: boolean;
  onOpen: (id: string) => void;
}): ReactElement {
  return (
    <Button
      className={s.topic}
      onPress={() => {
        onOpen(topic.id);
      }}
    >
      {!showCategory && (
        <span className={s.topicIcon} aria-hidden="true">
          <Icon name="book" size={16} />
        </span>
      )}
      <span className={s.topicText}>
        {showCategory && <span className={cx('eyebrow', s.topicCategory)}>{topic.category}</span>}
        <span className={s.topicTitle}>{topic.title}</span>
        <span className={s.topicSummary}>{topic.summary}</span>
      </span>
    </Button>
  );
}

function TopicLinks({
  topics,
  onOpen,
}: {
  topics: readonly HelpTopic[];
  onOpen: (id: string) => void;
}): ReactElement {
  return (
    <ul className={s.plainLinks}>
      {topics.map((topic) => (
        <li key={topic.id}>
          <Button
            className={s.linkButton}
            onPress={() => {
              onOpen(topic.id);
            }}
          >
            {topic.title}
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** Server search when there is one (debounced, cancelled when the words change); else local. */
function useTopicSearch(
  query: string,
  topics: readonly HelpTopic[],
  search: HelpSource['search'],
): { results: readonly HelpTopic[]; searching: boolean; failed: boolean } {
  type SearchState = { query: string; search: HelpSource['search'] };
  const [remote, setRemote] = useState<(SearchState & { results: readonly HelpTopic[] }) | null>(
    null,
  );
  const [failed, setFailed] = useState<SearchState | null>(null);
  useEffect(() => {
    if (search === undefined || query === '') return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      search(query, { signal: controller.signal }).then(
        (results) => {
          if (!controller.signal.aborted) {
            setRemote({ query, search, results });
            setFailed(null);
          }
        },
        () => {
          // A failed fresh search must not expose cached topics as current results.
          if (!controller.signal.aborted) setFailed({ query, search });
        },
      );
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, search]);
  const local = useMemo(
    () => (query === '' ? [] : topics.filter((topic) => matches(topic, query))),
    [query, topics],
  );
  if (search === undefined || (failed?.query === query && failed.search === search))
    return {
      results: search === undefined ? local : [],
      searching: false,
      failed: search !== undefined,
    };
  if (remote?.query === query && remote.search === search)
    return { results: remote.results, searching: false, failed: false };
  return { results: [], searching: true, failed: false };
}

function BrowseHelp({
  page,
  topics,
  source,
  contact,
  onFeedback,
  openId,
  onOpen,
  onAskChat,
  onShowMe,
}: {
  page: PageHelp;
  topics: readonly HelpTopic[];
  source?: HelpSource;
  contact?: HelpContact;
  onFeedback?: () => void;
  openId: string | null;
  onOpen: (id: string | null) => void;
  onAskChat?: () => void;
  onShowMe?: (anchor: HelpAnchorRef) => boolean;
}): ReactElement {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const articleHeadingRef = useRef<HTMLHeadingElement>(null);
  const byId = useMemo(() => new Map(topics.map((topic) => [topic.id, topic])), [topics]);
  const open = openId === null ? undefined : byId.get(openId);
  const trimmed = query.trim();
  const { results, searching, failed } = useTopicSearch(trimmed, topics, source?.search);

  useEffect(() => {
    if (open) articleHeadingRef.current?.focus();
  }, [open]);

  const status = source?.status ?? 'ready';
  const pageIds =
    source?.pageTopicIds !== undefined && source.pageTopicIds.length > 0
      ? source.pageTopicIds
      : page.topicIds;
  const pageTopics = pageIds
    .map((id) => byId.get(id))
    .filter((topic): topic is HelpTopic => topic !== undefined);
  const popular = (
    source?.popularIds !== undefined
      ? source.popularIds
          .map((id) => byId.get(id))
          .filter((topic): topic is HelpTopic => topic !== undefined)
      : topics
  )
    .filter((topic) => !pageIds.includes(topic.id))
    .slice(0, 4);
  const categories = useMemo(() => {
    const groups = new Map<string, HelpTopic[]>();
    for (const topic of topics)
      groups.set(topic.category, [...(groups.get(topic.category) ?? []), topic]);
    return [...groups.entries()];
  }, [topics]);

  const contactCard = (contact || onFeedback) && (
    <>
      {contact && onFeedback && (
        <p className={s.separate}>
          Questions about an award or a process? Ask the Stock Admin team.
        </p>
      )}
      {contact && <ContactCard contact={contact} />}
      {onFeedback && <FeedbackCard onFeedback={onFeedback} />}
    </>
  );

  if (open && status === 'ready') {
    const related = (open.related ?? [])
      .map((id) => byId.get(id))
      .filter((topic): topic is HelpTopic => topic !== undefined);
    // Without the chat too: a topic about something on this page can point at it (APP-090).
    const anchor = (source?.pageAnchors ?? []).find((a) => open.show?.includes(a.id) === true);
    return (
      <>
        <div className={s.article}>
          <Button
            className={s.linkButton}
            onPress={() => {
              onOpen(null);
            }}
          >
            <Icon name="arrowLeft" size={16} />
            {trimmed === '' ? 'All help for this page' : 'Back to results'}
          </Button>
          <HelpArticle
            topic={open}
            related={related}
            headingRef={articleHeadingRef}
            onOpen={onOpen}
            {...(anchor && { anchor })}
            {...(onShowMe && { onShowMe })}
          />
        </div>
        {contactCard}
      </>
    );
  }

  const availability =
    status === 'loading' ? (
      <p className={s.loading} role="status">
        <Icon name="book" size={16} /> Loading help topics…
      </p>
    ) : (
      <EmptyState
        variant="first-day"
        art="search"
        size="sm"
        headingLevel={3}
        headline="Help topics didn’t load"
        body="Check your connection and try again. The Stock Admin team can help in the meantime."
        {...(source?.onRetry && { action: { label: 'Try again', onPress: source.onRetry } })}
      />
    );

  if (openId !== null && status !== 'ready')
    return (
      <>
        {availability}
        {contactCard}
      </>
    );

  if (openId !== null)
    return (
      <>
        {/* A designed state, with its one way on (APP-093). */}
        <div role="status">
          <EmptyState
            variant="first-day"
            art="search"
            size="sm"
            headingLevel={3}
            headline="This article is no longer available"
            body="It may have moved or been retired. Go back to Help to search or browse the topics for this page."
            action={{
              label: 'Back to help',
              onPress: () => {
                onOpen(null);
              },
            }}
          />
        </div>
        {contactCard}
      </>
    );

  return (
    <>
      {source?.manageHref !== undefined && (
        <ButtonLink href={source.manageHref} variant="ghost" size="sm">
          Manage help articles
        </ButtonLink>
      )}
      <SearchField value={query} onChange={setQuery} className={s.search}>
        <Label className={s.searchLabel}>Search help</Label>
        <span className={s.searchBox}>
          <span className={s.searchIcon}>
            <Icon name="search" size={16} />
          </span>
          <Input className={s.searchInput} placeholder="Try “election” or “budget”" />
        </span>
      </SearchField>

      {status !== 'ready' ? (
        availability
      ) : trimmed === '' ? (
        <div className={s.group}>
          {pageTopics.length > 0 && (
            <>
              <h3 className={cx('eyebrow', s.groupLabel)}>For this page · {page.pageLabel}</h3>
              <ul className={s.topicList}>
                {pageTopics.map((topic) => (
                  <li key={topic.id}>
                    <TopicButton topic={topic} showCategory={false} onOpen={onOpen} />
                  </li>
                ))}
              </ul>
            </>
          )}
          {popular.length > 0 && (
            <>
              <h3 className={cx('eyebrow', s.groupLabel)}>Popular</h3>
              <TopicLinks topics={popular} onOpen={onOpen} />
            </>
          )}
          {topics.length > 0 ? (
            <>
              <Button
                className={s.linkButton}
                aria-expanded={showAll}
                onPress={() => {
                  setShowAll((value) => !value);
                }}
              >
                <Icon name={showAll ? 'chevronUp' : 'chevronDown'} size={16} />
                All help topics
              </Button>
              {showAll &&
                categories.map(([category, list]) => (
                  <div key={category} className={s.category}>
                    <h4 className={cx('eyebrow', s.groupLabel)}>{category}</h4>
                    <TopicLinks topics={list} onOpen={onOpen} />
                  </div>
                ))}
            </>
          ) : (
            <EmptyState
              variant="first-day"
              art="search"
              size="sm"
              headingLevel={3}
              headline="No help topics yet"
              body="Help topics for your roles show here. The Stock Admin team can answer questions in the meantime."
            />
          )}
        </div>
      ) : (
        <div className={s.group} aria-live="polite" aria-busy={searching}>
          <h3 className={cx('eyebrow', s.groupLabel)}>
            {failed
              ? 'Search didn’t load. Try changing your search or reopening Help.'
              : searching
                ? 'Searching…'
                : results.length === 0
                  ? 'Search results'
                  : results.length === 1
                    ? '1 topic'
                    : `${String(results.length)} topics`}
          </h3>
          {results.length > 0 ? (
            <ul className={s.topicList}>
              {results.map((topic) => (
                <li key={topic.id}>
                  <TopicButton topic={topic} showCategory onOpen={onOpen} />
                </li>
              ))}
            </ul>
          ) : (
            !searching &&
            !failed && (
              <EmptyState
                variant="first-day"
                art="search"
                size="sm"
                headingLevel={4}
                headline={`No help topics match “${trimmed}”`}
                body="Try fewer words, like “budget” or “letter”. Or contact the Stock Admin team below."
                {...(onAskChat && { action: { label: 'Ask instead', onPress: onAskChat } })}
              />
            )
          )}
        </div>
      )}
      {contactCard}
    </>
  );
}

/** Feedback about MyRewards itself, kept apart from questions for the Stock Admin team. */
function FeedbackCard({ onFeedback }: { onFeedback: () => void }): ReactElement {
  return (
    <section
      className={s.contact}
      aria-label="Feedback about MyRewards"
      data-testid="help-feedback"
    >
      <span className={s.contactIcon} aria-hidden="true">
        <Icon name="message" size={18} />
      </span>
      <div className={s.contactText}>
        <p className={s.contactBody}>
          Something not working, or an idea for MyRewards? Send feedback.
        </p>
        <Button className={s.linkButton} onPress={onFeedback}>
          Send feedback about MyRewards
        </Button>
      </div>
    </section>
  );
}

/** "Still stuck?": the Stock Admin team by name, its mailbox, and a message sent from Help. */
function ContactCard({ contact }: { contact: HelpContact }): ReactElement {
  const [writing, setWriting] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<readonly string[] | null>(null);
  const titleId = useId();
  const { onSend } = contact;
  const body =
    contact.team.length > 0
      ? `The Stock Admin team (${namesInWords(contact.team)}) answers questions about awards, elections and letters.`
      : contact.body;
  return (
    <section className={s.contact} aria-labelledby={titleId}>
      <span className={s.contactIcon} aria-hidden="true">
        <Icon name="mail" size={18} />
      </span>
      <div className={s.contactText}>
        <h3 id={titleId} className={s.contactTitle}>
          Still stuck?
        </h3>
        <p className={s.contactBody}>{body}</p>
        {contact.email !== null && (
          <a className={s.contactLink} href={`mailto:${contact.email}`}>
            Email {contact.email}
          </a>
        )}
        {sentTo !== null && (
          <p className={s.sent} role="status">
            <Icon name="check" size={16} /> Sent to {namesInWords(sentTo)}. They’ll reply by email.
          </p>
        )}
        {onSend !== undefined && !writing && (
          <Button
            className={s.linkButton}
            onPress={() => {
              setWriting(true);
              setSentTo(null);
            }}
          >
            Write to the Stock Admin team
          </Button>
        )}
        {onSend !== undefined && writing && (
          <Form
            className={s.writeForm}
            onSubmit={(event) => {
              event.preventDefault();
              if (subject.trim() === '' || message.trim() === '' || pending) return;
              setPending(true);
              setError(null);
              onSend({ subject: subject.trim(), message: message.trim() }).then(
                (to) => {
                  setPending(false);
                  setSentTo(to);
                  setWriting(false);
                  setSubject('');
                  setMessage('');
                },
                (failure: unknown) => {
                  setPending(false);
                  setError(
                    failure instanceof Error && failure.message !== ''
                      ? failure.message
                      : 'We couldn’t send that. Try again in a moment.',
                  );
                },
              );
            }}
          >
            <TextField
              label="Subject"
              value={subject}
              onChange={setSubject}
              isRequired
              maxLength={200}
            />
            <TextField
              label="Message"
              multiline
              value={message}
              onChange={setMessage}
              isRequired
              maxLength={5000}
            />
            {error !== null && (
              <p className={s.error} role="alert">
                {error}
              </p>
            )}
            <div className={s.formActions}>
              <UiButton type="submit" variant="navy" size="sm" isDisabled={pending}>
                {pending ? 'Sending…' : 'Send'}
              </UiButton>
              <UiButton
                variant="ghost"
                size="sm"
                onPress={() => {
                  setWriting(false);
                  setError(null);
                }}
              >
                Cancel
              </UiButton>
            </div>
          </Form>
        )}
      </div>
    </section>
  );
}
